import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PaymentHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPayments();
  }, [user]);

  const fetchPayments = async () => {
    try {
      const data = await api.getPaymentHistory();
      setPayments(data);
    } catch (error) {
      toast.error('Error al cargar historial de pagos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5" style={{ color: '#10B981' }} />;
      case 'pending':
        return <Clock className="w-5 h-5" style={{ color: '#F59E0B' }} />;
      default:
        return <XCircle className="w-5 h-5" style={{ color: '#EF4444' }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Pagado';
      case 'pending':
        return 'Pendiente';
      default:
        return 'Fallido';
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="contractcheck360" className="h-10 w-auto" />
              <span className="text-xl brand-name" style={{ color: '#1A4D2E' }}>
                contractcheck360
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline">Volver al Dashboard</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-6xl py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
          Historial de Pagos
        </h1>
        <p className="text-lg mb-8" style={{ color: '#64748B' }}>Todas tus transacciones</p>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-white border border-border shadow-sm rounded-sm p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4" style={{ color: '#64748B' }} />
            <p className="text-lg mb-4" style={{ color: '#64748B' }}>Aún no tienes pagos registrados</p>
            <Link to="/upload">
              <Button className="bg-primary text-primary-foreground" style={{ backgroundColor: '#1A4D2E' }}>
                Subir Contrato
              </Button>
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-border shadow-sm rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b" style={{ backgroundColor: '#F5F5F0' }}>
                  <tr>
                    <th className="text-left p-4 font-semibold">Fecha</th>
                    <th className="text-left p-4 font-semibold">Monto</th>
                    <th className="text-left p-4 font-semibold">Tipo</th>
                    <th className="text-left p-4 font-semibold">Estado</th>
                    <th className="text-left p-4 font-semibold">ID Transacción</th>
                  </tr>
                </thead>

                <tbody>
                  {(() => {
                    const successfulPayments = payments.filter(p => p.payment_status === 'completed');

                    if (successfulPayments.length === 0) {
                      return (
                        <tr>
                          <td colSpan="5" className="p-6 text-center text-gray-500">
                            No tienes pagos completados aún.
                          </td>
                        </tr>
                      );
                    }

                    return successfulPayments.map((payment, index) => (
                      <tr key={payment.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="p-4">
                          {new Date(payment.created_at).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="p-4 font-semibold">
                          ${payment.amount} {payment.currency.toUpperCase()}
                        </td>
                        <td className="p-4">
                          {payment.metadata?.package_id === 'analysis' ? 'Análisis' :
                           payment.metadata?.package_id === 'comparison_2' ? 'Comparación (2)' :
                           payment.metadata?.package_id === 'comparison_3' ? 'Comparación (3)' : 'N/A'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(payment.payment_status)}
                            <span>{getStatusText(payment.payment_status)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm" style={{ color: '#64748B' }}>
                          {payment.id.substring(0, 8)}...
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>

              </table>
            </div>

            <div className="p-6 border-t" style={{ backgroundColor: '#F5F5F0' }}>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Pagado:</span>
                <span className="text-2xl font-bold" style={{ color: '#1A4D2E' }}>
                  ${payments.filter(p => p.payment_status === 'completed').reduce((sum, p) => sum + p.amount, 0).toFixed(2)} MXN
                </span>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
